import dayjs from 'dayjs'
import pluginDuration from 'dayjs/plugin/duration'
import { computed, isRef, Ref, ref } from 'vue'

if (!(typeof dayjs.duration === 'function')) {
  dayjs.extend(pluginDuration)
}

export type TimeTrackable =
  | dayjs.ConfigType
  | Ref<dayjs.ConfigType>
  | (() => dayjs.ConfigType)

export const useTimeDiff = () => {
  // Helper functions for dayjs stuff
  const toDifference = (time?: dayjs.ConfigType) => dayjs().diff(time)
  const toDuration = (
    time: number,
    unit?: 'second' | 'minute' | 'hour' | 'day'
  ) => dayjs.duration(time ? time : 0, unit)

  // Get duration as a string 'dd:HH:mm:ss' | '30:23:59:59'
  const toDurationString = (duration: pluginDuration.Duration) => {
    const seconds = duration.get('seconds').toString().padStart(2, '0')
    const minutes = duration.get('minutes').toString().padStart(2, '0')
    const hours = duration.get('hours').toString().padStart(2, '0')
    const days = duration.get('days').toString().padStart(2, '0')

    let timeString = `${minutes}:${seconds}`

    if (hours !== '00' || days !== '00') {
      timeString = `${hours}:${timeString}`
    }

    if (days !== '00') {
      timeString = `${days}:${timeString}`
    }

    return timeString
  }

  class DifferenceTracker {
    readonly tracking = ref<TimeTrackable>()
    readonly difference = ref<number>(0)
    readonly duration = computed(() => toDuration(this.difference.value))
    readonly durationString = computed(() =>
      toDurationString(this.duration.value)
    )

    async track(
      startTime?: TimeTrackable,
      trackWhile: Ref<boolean> | (() => boolean) = () => true
    ) {
      const shouldTrack = isRef(trackWhile)
        ? () => trackWhile.value
        : trackWhile

      this.tracking.value = startTime

      while (
        shouldTrack() &&
        this.tracking.value &&
        this.tracking.value === startTime
      ) {
        let start = startTime as TimeTrackable

        // Convert Ref or Function to dayjs.ConfigType
        if (isRef(start)) {
          start = start.value
        } else if (typeof start === 'function') {
          start = start()
        }

        this.difference.value = toDifference(start)

        await new Promise((res) => setTimeout(res, 1000))
      }
    }

    stop(clear = false) {
      this.tracking.value = void 0

      if (clear) {
        this.difference.value = 0
      }
    }
  }

  return {
    DifferenceTracker,
    toDifference,
    toDuration,
    toDurationString,
  }
}
